import { useMemo } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { PositionInventoryData } from "~/types/wms";

interface MiniRackGridProps {
  rackCode: string;
  positions: PositionInventoryData[];
  selectedPositionId?: string | null;
  onPositionClick: (positionId: string) => void;
}

type PositionStatus = "empty" | "occupied" | "partial" | "reserved";

function getPositionStatus(
  positionInventory: PositionInventoryData
): PositionStatus {
  if (!positionInventory.hasStock) {
    return "empty";
  }
  if (positionInventory.reservedBoxes >= positionInventory.totalBoxes) {
    return "reserved";
  }
  if (positionInventory.reservedBoxes > 0) {
    return "partial";
  }
  return "occupied";
}

function getStatusColor(status: PositionStatus): string {
  switch (status) {
    case "empty":
      return "bg-gray-200 hover:bg-gray-300 border-gray-300";
    case "occupied":
      return "bg-green-300 hover:bg-green-400 border-green-500";
    case "partial":
      return "bg-yellow-300 hover:bg-yellow-400 border-yellow-500";
    case "reserved":
      return "bg-blue-300 hover:bg-blue-400 border-blue-500";
  }
}

export function MiniRackGrid({
  rackCode,
  positions,
  selectedPositionId,
  onPositionClick,
}: MiniRackGridProps) {
  // Sort positions by level (highest first) for vertical display
  const sortedPositions = useMemo(() => {
    if (!positions || positions.length === 0) {
      return [];
    }

    // Sort by level descending (top to bottom)
    return [...positions].sort((a, b) => {
      const levelA = a.position.level || 0;
      const levelB = b.position.level || 0;
      return levelB - levelA;
    });
  }, [positions]);

  if (!positions || positions.length === 0) {
    return (
      <div className="flex h-full w-12 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-400">
        -
      </div>
    );
  }

  const cellSize = 28; // pixels per cell

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col items-center">
        {/* Rack code at top */}
        <div className="mb-1 text-xs font-medium text-gray-600">{rackCode}</div>

        {/* Vertical stack of positions */}
        <div className="flex flex-col gap-0.5 rounded border border-gray-200 bg-white p-1">
          {sortedPositions.map((posData) => {
            const status = getPositionStatus(posData);
            const isSelected = selectedPositionId === posData.position.id;

            return (
              <Tooltip key={posData.position.id}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onPositionClick(posData.position.id)}
                    className={cn(
                      "rounded-sm border transition-all",
                      getStatusColor(status),
                      isSelected && "ring-2 ring-primary ring-offset-1"
                    )}
                    style={{ width: cellSize, height: cellSize }}
                    aria-label={`Posición ${posData.position.code}`}
                  />
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs">
                  <div className="font-medium">{posData.position.code}</div>
                  {posData.hasStock ? (
                    <div className="text-muted-foreground">
                      {posData.availableBoxes} / {posData.totalBoxes} cajas
                    </div>
                  ) : (
                    <div className="text-muted-foreground">Vacío</div>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
